import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, DatabaseBackup, Trash2, Plus, X, Users, Eye, EyeOff, RefreshCw, Lock, Save, Upload } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { http, apiError } from "@/lib/api";
import { downloadBlob } from "@/lib/exporters";
import { ClinicMark } from "@/components/Brand";
import { ConsultantForm, DeleteConsultantDialog } from "@/components/ConsultantFields";
import { toast } from "sonner";

export default function Settings() {
  const { clinic, setClinic, isOwner, viewMode, setMode, user, pullAll, data, activeConsultants, subscription } = useApp();
  const [brand, setBrand] = useState({ name: "", primary_color: "#2563EB", secondary_color: "#0EA5A4", logo_url: "" });
  const [newT, setNewT] = useState("");
  const [deletingC, setDeletingC] = useState(null);
  const [staff, setStaff] = useState([]);
  const [backups, setBackups] = useState([]);
  const [showCode, setShowCode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo too large — use an image under 3MB");
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data: res } = await http.post("/upload/logo", body);
      setClinic(res.clinic);
      setBrand((b) => ({ ...b, logo_url: res.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (clinic) setBrand({ name: clinic.name || "", primary_color: clinic.primary_color || "#2563EB",
      secondary_color: clinic.secondary_color || "#0EA5A4", logo_url: clinic.logo_url || "" });
  }, [clinic]);

  useEffect(() => {
    if (!isOwner) return;
    http.get("/clinic/staff").then(({ data }) => setStaff(data)).catch(() => {});
    http.get("/backup/list").then(({ data }) => setBackups(data)).catch(() => {});
  }, [isOwner]);

  const putClinic = async (patch, msg) => {
    try {
      const { data: res } = await http.put("/clinic", { ...clinic, ...patch });
      setClinic(res);
      toast.success(msg);
    } catch (e) { toast.error(apiError(e)); }
  };

  const regenerate = async () => {
    try {
      const { data: res } = await http.post("/clinic/staff-code");
      setClinic(res);
      setShowCode(true);
      toast.success("New staff code generated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportJson = async () => {
    try {
      const { data: res } = await http.get("/backup/export");
      downloadBlob(new Blob([JSON.stringify(res, null, 2)], { type: "application/json" }), `dentmemo-backup-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const runBackup = async () => {
    try {
      await http.post("/backup/run");
      const { data: res } = await http.get("/backup/list");
      setBackups(res);
      toast.success("Cloud backup created");
    } catch (e) { toast.error(apiError(e)); }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Backup file too large — must be under 50MB");
    if (!window.confirm(
      `Restore "${file.name}" into this clinic? Patients, visits, consultants and settlements from the ` +
      "file will be added or updated here. Nothing already in this clinic will be deleted."
    )) return;
    setRestoring(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data: res } = await http.post("/backup/import", body);
      const total = Object.values(res.imported || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${total} record${total === 1 ? "" : "s"} from backup`);
      await pullAll();
    } catch (err) {
      toast.error(err?.response?.status === 413 ? "Backup file too large for the server — must be under 50MB" : apiError(err));
    } finally {
      setRestoring(false);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm("Delete ALL patients, visits, consultants and settlements for this clinic? This cannot be undone.")) return;
    try {
      await http.delete("/account/data");
      await pullAll();
      toast.success("Clinic data deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const treatments = clinic?.treatments || [];

  const billingLine = (() => {
    if (!subscription) return "Loading…";
    switch (subscription.status) {
      case "trialing":
        return `Free trial · ${subscription.days_left} day${subscription.days_left === 1 ? "" : "s"} left`;
      case "active":
        return `Active · renews ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString("en-IN") : "—"}`;
      case "past_due":
        return "Payment failed — we're retrying";
      case "cancelled":
        return `Cancelled · access until ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString("en-IN") : "—"}`;
      case "expired":
        return subscription.billing_cycle ? "Your subscription has ended" : "Your trial has ended";
      default:
        return subscription.status || "—";
    }
  })();

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="dm-page-title">Clinic <span className="font-extrabold">settings</span></h1>
      </div>

      <section className="dm-panel p-5 md:p-7" data-testid="brand-card">
        <h2 className="font-head text-lg font-semibold text-slate-800">Clinic Branding</h2>
        <div className="mt-5 grid gap-5 md:grid-cols-[auto_1fr_auto_auto_auto] md:items-end">
          <ClinicMark logo={brand.logo_url} size={56} />
          <div>
            <p className="label-xs mb-1.5">Clinic name</p>
            <input data-testid="brand-name" className="dm-input" placeholder="Clinic name" value={brand.name}
              onChange={(e) => setBrand({ ...brand, name: e.target.value })} disabled={!isOwner} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Primary color</p>
            <input type="color" data-testid="brand-primary" className="dm-input h-11 w-20 p-1" value={brand.primary_color}
              onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} disabled={!isOwner} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Secondary color</p>
            <input type="color" data-testid="brand-secondary" className="dm-input h-11 w-20 p-1" value={brand.secondary_color}
              onChange={(e) => setBrand({ ...brand, secondary_color: e.target.value })} disabled={!isOwner} />
          </div>
          {isOwner && <button data-testid="brand-save" onClick={() => putClinic(brand, "Clinic branding saved")} className="dm-btn-primary"><Save size={15} /> Save</button>}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className={`dm-btn-ghost ${isOwner ? "cursor-pointer" : "pointer-events-none opacity-40"}`}>
            <Upload size={15} /> {uploading ? "Uploading…" : "Upload logo"}
            <input type="file" accept="image/*" data-testid="brand-logo-file" className="hidden" onChange={uploadLogo} disabled={!isOwner || uploading} />
          </label>
          <span className="text-xs text-slate-500">PNG or JPG, under 3MB — shown across every screen.</span>
        </div>
        <div className="mt-4">
          <p className="label-xs mb-1.5">…or paste a logo URL</p>
          <input data-testid="brand-logo-input" className="dm-input" placeholder="https://…" value={brand.logo_url || ""}
            onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} disabled={!isOwner} />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="dm-panel p-5 md:p-7">
          <h2 className="font-head text-lg font-semibold text-slate-800">Treatments</h2>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="treatment-tags">
            {treatments.map((t) => (
              <span key={t} className="dm-chip">
                {t}
                {isOwner && (
                  <button data-testid={`remove-treatment-${t.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => putClinic({ treatments: treatments.filter((x) => x !== t) }, "Treatments updated")}
                    className="text-slate-500 transition-colors hover:text-red-600"><X size={13} /></button>
                )}
              </span>
            ))}
          </div>
          {isOwner && (
            <div className="mt-4 flex gap-2">
              <input data-testid="new-treatment" className="dm-input" placeholder="Add new treatment…" value={newT} onChange={(e) => setNewT(e.target.value)} />
              <button data-testid="add-treatment-btn" onClick={() => { if (newT.trim()) { putClinic({ treatments: [...treatments, newT.trim()] }, "Treatments updated"); setNewT(""); } }} className="dm-btn-primary"><Plus size={15} /> Add</button>
            </div>
          )}
        </section>

        <section className="dm-panel p-5 md:p-7">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-head text-lg font-semibold text-slate-800">Consultants</h2>
            <Link to="/app/consultants" data-testid="consultants-link" className="text-sm font-semibold dm-accent-text">Settlements →</Link>
          </div>
          <ul className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200" data-testid="consultant-tags">
            {activeConsultants.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">{c.name}</p>
                  <p className="truncate text-xs text-slate-500">{c.specialty || "—"}{c.phone ? ` · ${c.phone}` : ""}</p>
                </div>
                {isOwner && (
                  <button data-testid={`remove-consultant-${c.id}`} onClick={() => setDeletingC(c)} aria-label={`Delete ${c.name}`}
                    className="text-slate-500 transition-colors hover:text-red-600"><Trash2 size={15} /></button>
                )}
              </li>
            ))}
            {activeConsultants.length === 0 && <li className="px-4 py-2.5 text-sm text-slate-500">No consultants yet.</li>}
          </ul>
          <ConsultantForm className="mt-4" compact />
          <DeleteConsultantDialog consultant={deletingC} onClose={() => setDeletingC(null)} />
        </section>

        <section className="dm-panel p-5 md:p-7">
          <h2 className="font-head text-lg font-semibold text-slate-800">Staff code</h2>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span data-testid="staff-code" className="font-mono text-2xl font-semibold tracking-[0.35em] text-slate-800">
              {showCode ? clinic?.staff_code || "—" : "••••"}
            </span>
            <button data-testid="toggle-staff-code" onClick={() => setShowCode(!showCode)} className="dm-btn-quiet !py-1.5 !text-xs">
              {showCode ? <EyeOff size={13} /> : <Eye size={13} />} Show
            </button>
            <button data-testid="copy-staff-code" onClick={() => { navigator.clipboard.writeText(clinic?.staff_code || ""); toast.success("Copied"); }} className="dm-btn-quiet !py-1.5 !text-xs"><Copy size={13} /> Copy</button>
            {isOwner && <button data-testid="regen-staff-code" onClick={regenerate} className="dm-btn-ghost !py-1.5 !text-xs"><RefreshCw size={13} /> Generate a new code</button>}
          </div>
        </section>

        <section className="dm-panel p-5 md:p-7">
          <h2 className="font-head text-lg font-semibold text-slate-800">Access</h2>
          <p className="mt-2 text-sm text-slate-600">
            {user?.role === "owner" ? "Owner" : "Assistant"} · {viewMode === "doctor" ? "Doctor Access" : "Assistant Mode"}
          </p>
          <button data-testid="lock-assistant-btn" onClick={() => { setMode("assistant"); toast.success("Locked to Assistant Mode"); }}
            disabled={viewMode !== "doctor"} className="dm-btn-ghost mt-4 disabled:opacity-40"><Lock size={15} /> 🔒 Lock to Assistant Mode</button>
        </section>
      </div>

      {isOwner && (
        <>
          <section className="dm-panel p-5 md:p-7" data-testid="settings-billing-card">
            <h2 className="font-head text-lg font-semibold text-slate-800">Billing</h2>
            <p className="mt-2 text-sm text-slate-600" data-testid="settings-billing-status">{billingLine}</p>
            <Link to="/app/billing" data-testid="manage-billing-link" className="dm-btn-ghost mt-4">Manage billing</Link>
          </section>

          <section className="dm-panel p-5 md:p-7">
            <h2 className="font-head text-lg font-semibold text-slate-800"><Users size={16} className="mr-1.5 inline text-slate-500" /> Team</h2>
            <ul className="mt-4 max-h-[50vh] divide-y divide-slate-200 overflow-auto" data-testid="staff-list">
              {staff.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0 truncate text-slate-800">{s.name} <span className="text-xs text-slate-500">{s.email}</span></span>
                  <span className={`dm-badge ${s.role === "owner" ? "dm-badge-accent" : "border-slate-300 bg-slate-100 text-slate-600"}`}>{s.role}</span>
                </li>
              ))}
              {staff.length === 0 && <li className="py-2 text-sm text-slate-500">Share the staff code above so assistants can join.</li>}
            </ul>
          </section>

          <section className="dm-panel p-5 md:p-7">
            <h2 className="font-head text-lg font-semibold text-slate-800">Backups &amp; data</h2>
            <p className="mt-2 text-sm text-slate-600">A cloud backup runs automatically every 24 hours. Download a JSON copy anytime.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button data-testid="export-json-btn" onClick={exportJson} className="dm-btn-ghost"><Download size={15} /> Download JSON</button>
              <button data-testid="run-backup-btn" onClick={runBackup} className="dm-btn-ghost"><DatabaseBackup size={15} /> Back up now</button>
            </div>
            {backups.length > 0 && (
              <ul className="mt-4 space-y-1 font-mono text-xs text-slate-500" data-testid="backup-list">
                {backups.slice(0, 5).map((b) => <li key={b.id}>{new Date(b.created_at).toLocaleString()}</li>)}
              </ul>
            )}

            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="text-sm font-semibold text-slate-800">Restore from a backup file</p>
              <p className="mt-1 text-sm text-slate-600">
                Coming from an older install, or switching devices? Upload a DentMemo JSON backup to bring your
                patients, visits, consultants and settlements into this clinic. Existing records here are kept;
                nothing is deleted.
              </p>
              <label className="dm-btn-ghost mt-4 cursor-pointer">
                <Upload size={15} /> {restoring ? "Restoring…" : "Upload backup JSON"}
                <input type="file" accept="application/json,.json" data-testid="restore-backup-file"
                  className="hidden" onChange={restoreBackup} disabled={restoring} />
              </label>
            </div>
          </section>

          <section className="dm-panel border-red-200 p-5 md:p-7">
            <h2 className="font-head text-lg font-semibold text-red-700">Danger zone (DPDP)</h2>
            <p className="mt-2 text-sm text-slate-600">Erase all patient records for this clinic. Consent was recorded at signup; deletion is permanent.</p>
            <button data-testid="delete-data-btn" onClick={deleteAll} className="dm-btn-danger mt-5"><Trash2 size={15} /> Delete all clinic data</button>
          </section>
        </>
      )}
    </div>
  );
}
